import { useQuery } from '@tanstack/react-query';
import { api } from '../api/client';
import { closeReadinessApi } from '../api/aiAuthority';
import { getWorkspaceId } from '../api/workspaces';

interface ScannerResult {
  type: string;
  severity: 'critical' | 'warning' | 'info';
  title: string;
  description: string;
}

interface ScannerSummary {
  totalIssues: number;
  criticalCount: number;
  warningCount: number;
  infoCount: number;
  results: ScannerResult[];
}

interface AIStatusBarProps {
  onClick: () => void;
}

export default function AIStatusBar({ onClick }: AIStatusBarProps) {
  const hasWorkspace = !!getWorkspaceId();

  const { data: scannerData } = useQuery<ScannerSummary>({
    queryKey: ['scanner-results'],
    queryFn: () => api.get('/scanners/summary'),
    staleTime: 60_000,
    retry: false,
    refetchOnWindowFocus: false,
    enabled: hasWorkspace,
  });

  const { data: readiness } = useQuery({
    queryKey: ['close-readiness-statusbar'],
    queryFn: closeReadinessApi.get,
    staleTime: 120_000,
    retry: false,
    refetchOnWindowFocus: false,
    enabled: hasWorkspace,
  });

  const criticalCount = scannerData?.criticalCount ?? 0;
  const warningCount = scannerData?.warningCount ?? 0;
  const totalIssues = scannerData?.totalIssues ?? 0;
  const readinessScore = readiness?.overallScore ?? -1;

  return (
    <div
      className="flex items-center gap-3 px-3 py-1 bg-gray-50 border-t border-gray-200 text-xs cursor-pointer hover:bg-gray-100 transition-colors"
      onClick={onClick}
      title="Click to open AI & Audit page"
    >
      <div className="flex items-center gap-1.5">
        <svg className="w-3.5 h-3.5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9.75 17L9 20l-1 1h8l-1-1-.75-3M3 13h18M5 17h14a2 2 0 002-2V5a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
        </svg>
        <span className="text-gray-500 font-medium">AI</span>
      </div>

      {totalIssues > 0 ? (
        <>
          {criticalCount > 0 && (
            <span className="flex items-center gap-1 text-red-600">
              <span className="w-1.5 h-1.5 rounded-full bg-red-500" />
              {criticalCount} critical
            </span>
          )}
          {warningCount > 0 && (
            <span className="flex items-center gap-1 text-amber-600">
              <span className="w-1.5 h-1.5 rounded-full bg-amber-500" />
              {warningCount} warnings
            </span>
          )}
          {scannerData?.infoCount && scannerData.infoCount > 0 && (
            <span className="flex items-center gap-1 text-blue-600">
              <span className="w-1.5 h-1.5 rounded-full bg-blue-500" />
              {scannerData.infoCount} info
            </span>
          )}
        </>
      ) : (
        <span className="text-gray-400">No issues detected</span>
      )}

      {/* Close readiness indicator */}
      {readinessScore >= 0 && (
        <span className={`flex items-center gap-1 ml-2 pl-2 border-l border-gray-300 ${
          readinessScore >= 80 ? 'text-green-600' : readinessScore >= 50 ? 'text-amber-600' : 'text-red-600'
        }`}>
          <svg className="w-3 h-3" viewBox="0 0 36 36" fill="none">
            <circle cx="18" cy="18" r="14" stroke="#e5e7eb" strokeWidth="4" />
            <circle cx="18" cy="18" r="14" stroke="currentColor" strokeWidth="4"
              strokeDasharray={`${readinessScore * 0.88} 88`} strokeLinecap="round"
              transform="rotate(-90 18 18)" />
          </svg>
          <span className="font-medium">{readinessScore}% ready</span>
        </span>
      )}

      <div className="flex-1" />
      <span className="text-gray-400">AI & Audit</span>
      <svg className="w-3 h-3 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
      </svg>
    </div>
  );
}
